import React from "react";
import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import { initKPConfig } from "./lib/kpConfig";
import { initDoNotTrack } from "./lib/doNotTrack";

// Initialize Do-Not-Track system first
initDoNotTrack();

// After a new deploy, browsers holding an old app shell request chunk files
// that no longer exist -> blank page. Reload once (guarded) to fetch the new build.
const RELOAD_KEY = 'chunk-reload-at';
const recoverFromStaleBuild = () => {
  const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
  if (Date.now() - last < 30000) return;
  sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  navigator.serviceWorker?.getRegistrations?.()
    .then((regs) => Promise.all(regs.map((r) => r.update().catch(() => {}))))
    .finally(() => window.location.reload());
};
window.addEventListener('vite:preloadError', (e) => {
  e.preventDefault();
  recoverFromStaleBuild();
});
window.addEventListener('unhandledrejection', (e) => {
  const msg = String((e.reason && e.reason.message) || e.reason || '');
  if (/dynamically imported module|Importing a module script failed|ChunkLoadError/i.test(msg)) {
    recoverFromStaleBuild();
  }
});

createRoot(document.getElementById("root")!).render(<App />);

// Defer global config fetch until the user actually interacts with the
// page (or after a long fallback). Cached config from localStorage is
// used in the meantime, so this never blocks LCP / the critical chain.
let configInitStarted = false;
const runConfigInit = () => {
  if (configInitStarted) return;
  configInitStarted = true;
  initKPConfig().catch((err) => {
    console.error('[App] Failed to init config:', err);
  });
};
const interactionEvents = ['pointerdown', 'keydown', 'scroll', 'touchstart'] as const;
interactionEvents.forEach((evt) => {
  window.addEventListener(evt, runConfigInit, { once: true, passive: true });
});
// Hard fallback so config still refreshes for users who never interact.
setTimeout(runConfigInit, 8000);

// No service worker: an old one served outdated pages after deploys.
// Remove any previously installed worker and its caches.
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations?.()
    .then((regs) => {
      if (!regs.length) return;
      regs.forEach((r) => r.unregister().catch(() => {}));
      if ('caches' in window) {
        caches.keys().then((keys) => keys.forEach((k) => caches.delete(k))).catch(() => {});
      }
    })
    .catch(() => {});
}
